import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GitCompare, ArrowRight, TrendingUp, TrendingDown, Minus,
  FileText, FolderOpen, ClipboardList, Search
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import api from '../services/api';
import './ReportComparison.css';

function ReportComparison() {
  const { t } = useTranslation(['reports', 'common']);
  const [periods, setPeriods] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [period1, setPeriod1] = useState('');
  const [period2, setPeriod2] = useState('');
  const [department, setDepartment] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    Promise.all([
      api.getPeriods(),
      api.getDepartments(),
    ]).then(([pRes, dRes]) => {
      setPeriods(pRes.data.results || pRes.data);
      setDepartments(dRes.data.results || dRes.data);
    }).catch(() => {});
  }, []);

  const handleCompare = async () => {
    if (!period1 || !period2) {
      alert(t('reports:comparison.selectTwoPeriodsAlert'));
      return;
    }
    if (period1 === period2) {
      alert(t('reports:comparison.selectDifferentPeriods'));
      return;
    }
    setLoading(true);
    try {
      const params = { period1, period2 };
      if (department) params.department = department;
      const res = await api.comparePeriods(params);
      setResult(res.data);
    } catch (error) {
      console.error('Compare error:', error);
      alert(t('reports:comparison.comparisonError'));
    } finally {
      setLoading(false);
    }
  };

  const renderChangeIndicator = (value, suffix = '') => {
    if (value === null || value === undefined) return <span className="change-neutral">—</span>;
    if (value > 0) return <span className="change-positive"><TrendingUp size={14} /> +{value}{suffix}</span>;
    if (value < 0) return <span className="change-negative"><TrendingDown size={14} /> {value}{suffix}</span>;
    return <span className="change-neutral"><Minus size={14} /> 0{suffix}</span>;
  };

  const buildChartData = () => {
    if (!result) return [];
    const { reports, documents, plans } = result;
    return [
      {
        name: t('reports:comparison.chartReports'),
        [result.period1.name]: reports.period1.total,
        [result.period2.name]: reports.period2.total,
      },
      {
        name: t('reports:comparison.chartApproved'),
        [result.period1.name]: reports.period1.approved,
        [result.period2.name]: reports.period2.approved,
      },
      {
        name: t('reports:comparison.chartDocuments'),
        [result.period1.name]: documents.period1.total,
        [result.period2.name]: documents.period2.total,
      },
      {
        name: t('reports:comparison.chartCompleted'),
        [result.period1.name]: documents.period1.completed,
        [result.period2.name]: documents.period2.completed,
      },
      {
        name: t('reports:comparison.chartPlans'),
        [result.period1.name]: plans.period1.total,
        [result.period2.name]: plans.period2.total,
      },
    ];
  };

  return (
    <div className="comparison-page">
      {/* Header */}
      <div className="comparison-header">
        <h1>
          <GitCompare size={24} />
          {t('reports:pageTitles.comparison')}
        </h1>
      </div>

      {/* Фильтры */}
      <div className="comparison-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>{t('reports:comparison.period1')}</label>
            <select
              className="filter-control"
              value={period1}
              onChange={e => setPeriod1(e.target.value)}
            >
              <option value="">{t('reports:comparison.selectPeriod')}</option>
              {periods.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.year})</option>
              ))}
            </select>
          </div>

          <div className="filter-arrow">
            <ArrowRight size={20} />
          </div>

          <div className="filter-group">
            <label>{t('reports:comparison.period2')}</label>
            <select
              className="filter-control"
              value={period2}
              onChange={e => setPeriod2(e.target.value)}
            >
              <option value="">{t('reports:comparison.selectPeriod')}</option>
              {periods.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.year})</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>{t('common:table.department')}</label>
            <select
              className="filter-control"
              value={department}
              onChange={e => setDepartment(e.target.value)}
            >
              <option value="">{t('reports:comparison.allDepartments')}</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name_ru}</option>
              ))}
            </select>
          </div>

          <div className="filter-group filter-action">
            <label>&nbsp;</label>
            <button
              className="btn-compare"
              onClick={handleCompare}
              disabled={loading || !period1 || !period2}
            >
              <Search size={16} />
              {loading ? t('reports:comparison.analyzing') : t('reports:comparison.compare')}
            </button>
          </div>
        </div>
      </div>

      {/* Результаты */}
      {result && (
        <>
          {/* Сводные карточки */}
          <div className="comparison-cards">
            {/* Отчёты */}
            <div className="cmp-card">
              <div className="cmp-card-header">
                <FileText size={20} />
                <span>{t('reports:comparison.reports')}</span>
              </div>
              <div className="cmp-card-body">
                <div className="cmp-row">
                  <span className="cmp-label">{t('reports:comparison.total')}</span>
                  <span className="cmp-val">{result.reports.period1.total}</span>
                  <span className="cmp-arrow">→</span>
                  <span className="cmp-val">{result.reports.period2.total}</span>
                  {renderChangeIndicator(result.reports.change.total_pct, '%')}
                </div>
                <div className="cmp-row">
                  <span className="cmp-label">{t('reports:comparison.approved')}</span>
                  <span className="cmp-val">{result.reports.period1.approved}</span>
                  <span className="cmp-arrow">→</span>
                  <span className="cmp-val">{result.reports.period2.approved}</span>
                  {renderChangeIndicator(result.reports.change.approved_pct, '%')}
                </div>
                <div className="cmp-row">
                  <span className="cmp-label">{t('reports:comparison.approvalPercent')}</span>
                  <span className="cmp-val">{result.reports.period1.approval_rate}%</span>
                  <span className="cmp-arrow">→</span>
                  <span className="cmp-val">{result.reports.period2.approval_rate}%</span>
                  {renderChangeIndicator(result.reports.change.approval_rate, 'п.п.')}
                </div>
              </div>
            </div>

            {/* Документы */}
            <div className="cmp-card">
              <div className="cmp-card-header">
                <FolderOpen size={20} />
                <span>{t('reports:comparison.documents')}</span>
              </div>
              <div className="cmp-card-body">
                <div className="cmp-row">
                  <span className="cmp-label">{t('reports:comparison.total')}</span>
                  <span className="cmp-val">{result.documents.period1.total}</span>
                  <span className="cmp-arrow">→</span>
                  <span className="cmp-val">{result.documents.period2.total}</span>
                  {renderChangeIndicator(result.documents.change.total_pct, '%')}
                </div>
                <div className="cmp-row">
                  <span className="cmp-label">{t('reports:comparison.completedDocs')}</span>
                  <span className="cmp-val">{result.documents.period1.completed}</span>
                  <span className="cmp-arrow">→</span>
                  <span className="cmp-val">{result.documents.period2.completed}</span>
                  {renderChangeIndicator(result.documents.change.completed)}
                </div>
                <div className="cmp-row">
                  <span className="cmp-label">{t('reports:comparison.completionPercent')}</span>
                  <span className="cmp-val">{result.documents.period1.completion_rate}%</span>
                  <span className="cmp-arrow">→</span>
                  <span className="cmp-val">{result.documents.period2.completion_rate}%</span>
                  {renderChangeIndicator(result.documents.change.completion_rate, 'п.п.')}
                </div>
              </div>
            </div>

            {/* Планы */}
            <div className="cmp-card">
              <div className="cmp-card-header">
                <ClipboardList size={20} />
                <span>{t('reports:comparison.plans')}</span>
              </div>
              <div className="cmp-card-body">
                <div className="cmp-row">
                  <span className="cmp-label">{t('reports:comparison.total')}</span>
                  <span className="cmp-val">{result.plans.period1.total}</span>
                  <span className="cmp-arrow">→</span>
                  <span className="cmp-val">{result.plans.period2.total}</span>
                  {renderChangeIndicator(result.plans.change.total)}
                </div>
                <div className="cmp-row">
                  <span className="cmp-label">{t('reports:comparison.avgCompletion')}</span>
                  <span className="cmp-val">{result.plans.period1.avg_completion}%</span>
                  <span className="cmp-arrow">→</span>
                  <span className="cmp-val">{result.plans.period2.avg_completion}%</span>
                  {renderChangeIndicator(result.plans.change.avg_completion, 'п.п.')}
                </div>
              </div>
            </div>
          </div>

          {/* График сравнения */}
          <div className="comparison-chart-card">
            <h2>{t('reports:comparison.visualComparison')}</h2>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={buildChartData()} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Legend />
                <Bar dataKey={result.period1.name} fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey={result.period2.name} fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Таблица метрик */}
          {result.metrics.length > 0 && (
            <div className="comparison-metrics-card">
              <h2>{t('reports:comparison.metricsComparison')}</h2>
              <table className="metrics-table">
                <thead>
                  <tr>
                    <th>{t('reports:comparison.indicator')}</th>
                    <th>{t('reports:comparison.unit')}</th>
                    <th>{result.period1.name}</th>
                    <th>{result.period2.name}</th>
                    <th>{t('reports:comparison.change')}</th>
                    <th>{t('reports:comparison.percentChange')}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.metrics.map((m, i) => (
                    <tr key={i}>
                      <td className="metric-name">{m.name}</td>
                      <td className="metric-unit">{m.unit}</td>
                      <td>{m.period1_value}</td>
                      <td>{m.period2_value}</td>
                      <td>{renderChangeIndicator(m.change)}</td>
                      <td>{renderChangeIndicator(m.change_pct, '%')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Пустое состояние */}
      {!result && !loading && (
        <div className="comparison-empty">
          <GitCompare size={48} />
          <h3>{t('reports:comparison.selectTwoPeriods')}</h3>
          <p>{t('reports:comparison.systemWillShowDiff')}</p>
        </div>
      )}
    </div>
  );
}

export default ReportComparison;
